import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CardActionArea from '@mui/material/CardActionArea';
import { alpha } from '@mui/material/styles';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro } from '@mui/x-data-grid-pro';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { fetchJobConfigurationsWithFilters } from '@sophocles/entities/JobConfiguration/actions';
import {
  fetchDashboardData,
  selectDashboardLoading,
  selectDashboardPendingJobs,
  selectDashboardRecentJobs,
  selectDashboardStats,
} from '@sophocles/features/projectServices';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { formatUserString } from '@sophocles/utils';

function getJobUrl(job, configMap) {
  const site = job.site;
  if (!site) return null;

  const siteGroup = _.get(site, ['site_groups', 0], null);
  if (!siteGroup) return null;

  const customerId = _.get(siteGroup, ['customer', 'id'], null);
  const projectId = configMap[customerId];
  const siteGroupId = siteGroup.id;
  const siteId = site.id;

  if (!customerId || !projectId || !siteGroupId || !siteId) return null;

  return `/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/site/${siteId}/job/${job.id}/`;
}

function PanelHeading({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.015em' }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function DashboardJobCardList({ jobs, configMap, t }) {
  const navigate = useNavigate();

  return (
    <Stack spacing={1} sx={{ px: 2.25, pb: 2 }}>
      {jobs.map(job => {
        const url = getJobUrl(job, configMap || {});
        const isPending = job.pending === true;
        const siteName = _.get(job, ['site', 'name'], t('projectServices.dashboard.unknownSite'));
        const siteGroupName = _.get(job, ['site', 'site_groups', 0, 'name'], '');
        const crewNumber = _.get(job, ['crew', 'crew_number'], null);

        return (
          <Card key={job.id} variant="outlined">
            <CardActionArea onClick={() => url && navigate(url)} disabled={!url}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {siteName}
                      </Typography>
                      {isPending ? (
                        <GridStatusCell label={t('projectServices.dashboard.status.pending')} color="warning" />
                      ) : (
                        <GridStatusCell label={t('projectServices.dashboard.status.completed')} color="ok" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {siteGroupName}
                      {crewNumber ? ` \u00B7 ${t('projectServices.dashboard.crewLabel', { crewNumber })}` : ''}
                      {job.installation_date ? ` \u00B7 ${job.installation_date}` : ''}
                    </Typography>
                  </Box>
                  <ChevronRightIcon color="action" sx={{ ml: 1, flexShrink: 0 }} />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
}

function JobTable({ jobs, title, loading, emptyMessage, configMap, subtitle, columns, isSmallScreen, t }) {
  const navigate = useNavigate();
  const theme = useTheme();

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: 2.25 }}>
          <PanelHeading title={title} subtitle={subtitle} />
          {[0, 1, 2].map(i => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: 2.25, pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <PanelHeading title={title} subtitle={subtitle} />
          <Chip label={`${jobs.length}`} size="small" variant="outlined" />
        </Stack>
      </CardContent>
      {jobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, px: 2.25, textAlign: 'center' }}>
          {title}
        </Typography>
      ) : isSmallScreen ? (
        <DashboardJobCardList jobs={jobs} configMap={configMap} t={t} />
      ) : (
        <DataGridPro
          rows={jobs}
          columns={columns}
          density="standard"
          getRowHeight={() => 'auto'}
          disableColumnFilter
          disableColumnSelector
          disableRowSelectionOnClick
          hideFooter
          onRowClick={params => {
            const url = getJobUrl(params.row, configMap || {});
            if (url) navigate(url);
          }}
          sx={{
            border: 0,
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              transition: 'background-color var(--duration-normal) var(--easing-standard)',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
            },
          }}
        />
      )}
    </Card>
  );
}

const toolbarButtonSx = {
  px: 2,
  py: 0.75,
  fontWeight: 600,
  fontSize: '0.875rem',
  borderWidth: 2,
  '&:hover': { borderWidth: 2 },
};

function Toolbar({ isCrewManager, isAccounting, t }) {
  const theme = useTheme();
  const showButtons = isCrewManager || isAccounting;
  if (!showButtons) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        px: 2.25,
        py: 1.25,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        borderColor: 'divider',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Button
        variant="outlined"
        color="primary"
        startIcon={<GroupsIcon />}
        component={Link}
        to="/services/crews/"
        sx={toolbarButtonSx}
      >
        {t('projectServices.dashboard.toolbar.crews')}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SettingsIcon />}
        component={Link}
        to="/services/definitions/"
        sx={toolbarButtonSx}
      >
        {t('projectServices.dashboard.toolbar.serviceDefinitions')}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AssessmentIcon />}
        component={Link}
        to="/services/reporting"
        sx={toolbarButtonSx}
      >
        {t('projectServices.dashboard.toolbar.reporting')}
      </Button>
    </Paper>
  );
}

function ProjectRow({ jobConfiguration, showTooltipInfo }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customer_term: customerTerm } = useSelector(selectTenantInfo);
  const projectId = _.get(jobConfiguration, ['id'], null);
  const customerId = _.get(jobConfiguration, ['customer', 'id'], null);

  if (!customerId || !projectId) return null;

  const customerName = _.get(
    jobConfiguration,
    ['customer', 'name'],
    t('projectServices.dashboard.unknownCustomer', { term: customerTerm }),
  );
  const workFormsUrl = `/services/customer/${customerId}/project/${projectId}/`;

  const tooltipTitle = showTooltipInfo
    ? t('projectServices.dashboard.customerProjectTooltip', { customerId, projectId })
    : '';

  return (
    <Tooltip title={tooltipTitle} disableHoverListener={!showTooltipInfo}>
      <Button
        component={Link}
        to={workFormsUrl}
        variant="outlined"
        color="inherit"
        fullWidth
        sx={{
          justifyContent: 'space-between',
          borderColor: 'divider',
          py: 1,
          px: 1.25,
          bgcolor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <ReceiptLongIcon sx={{ color: 'action.active' }} fontSize="small" />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {customerName}
          </Typography>
        </Stack>
        <ChevronRightIcon color="action" fontSize="small" />
      </Button>
    </Tooltip>
  );
}

function ProjectsSection({ jobConfigurations, projectsLoading, showTooltipInfo, t }) {
  if (projectsLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.25 }}>
          <PanelHeading
            title={t('projectServices.dashboard.activeProjects')}
            subtitle={t('projectServices.dashboard.activeProjectsSubtitle')}
          />
          <Stack spacing={1}>
            {[0, 1, 2].map(i => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (jobConfigurations.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <PanelHeading
            title={t('projectServices.dashboard.activeProjects')}
            subtitle={t('projectServices.dashboard.activeProjectsSubtitle')}
          />
          <Chip label={jobConfigurations.length} size="small" variant="outlined" />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          {jobConfigurations.map(jobConfiguration => (
            <ProjectRow
              key={jobConfiguration.id}
              jobConfiguration={jobConfiguration}
              showTooltipInfo={showTooltipInfo}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function StaffDashboard({
  loading,
  pendingJobs,
  recentJobs,
  stats,
  isCrewManager,
  isAccounting,
  configMap,
  jobConfigurations,
  projectsLoading,
  showTooltipInfo,
  t,
  jobColumns,
  isSmallScreen,
}) {
  return (
    <>
      <Toolbar isCrewManager={isCrewManager} isAccounting={isAccounting} t={t} />

      <ProjectsSection
        jobConfigurations={jobConfigurations}
        projectsLoading={projectsLoading}
        showTooltipInfo={showTooltipInfo}
        t={t}
      />

      <StatCardGroup columns={2} gap={2} sx={{ mb: 3 }}>
        <StatCard
          title={t('projectServices.dashboard.stats.pendingApprovals')}
          value={stats.pending_count ?? 0}
          color="warning.main"
          loading={loading}
          icon={<PendingActionsIcon fontSize="small" />}
          helperText={t('projectServices.dashboard.stats.pendingApprovalsHelper')}
        />
        <StatCard
          title={t('projectServices.dashboard.stats.completedThisWeek')}
          value={stats.completed_this_week ?? 0}
          color="success.main"
          loading={loading}
          icon={<TaskAltIcon fontSize="small" />}
          helperText={t('projectServices.dashboard.stats.completedThisWeekHelper')}
        />
      </StatCardGroup>

      <Stack spacing={3}>
        <JobTable
          title={t('projectServices.dashboard.pendingApprovals')}
          jobs={pendingJobs}
          loading={loading}
          emptyMessage={t('projectServices.dashboard.noPendingApprovals')}
          configMap={configMap}
          subtitle={t('projectServices.dashboard.pendingApprovalsSubtitle')}
          columns={jobColumns}
          isSmallScreen={isSmallScreen}
          t={t}
        />

        <JobTable
          title={t('projectServices.dashboard.recentlyCompleted')}
          jobs={recentJobs.slice(0, 10)}
          loading={loading}
          emptyMessage={t('projectServices.dashboard.noRecentCompletions')}
          configMap={configMap}
          subtitle={t('projectServices.dashboard.recentlyCompletedSubtitle')}
          columns={jobColumns}
          isSmallScreen={isSmallScreen}
          t={t}
        />
      </Stack>
    </>
  );
}

function TechnicianDashboard({
  loading,
  pendingJobs,
  recentJobs,
  configMap,
  jobConfigurations,
  projectsLoading,
  showTooltipInfo,
  t,
  jobColumns,
  isSmallScreen,
}) {
  return (
    <>
      <ProjectsSection
        jobConfigurations={jobConfigurations}
        projectsLoading={projectsLoading}
        showTooltipInfo={showTooltipInfo}
        t={t}
      />

      <Stack spacing={3}>
        <JobTable
          title={t('projectServices.dashboard.myPendingWork')}
          jobs={pendingJobs}
          loading={loading}
          emptyMessage={t('projectServices.dashboard.noPendingWork')}
          configMap={configMap}
          subtitle={t('projectServices.dashboard.myPendingWorkSubtitle')}
          columns={jobColumns}
          isSmallScreen={isSmallScreen}
          t={t}
        />

        <JobTable
          title={t('projectServices.dashboard.recentCompletions')}
          jobs={recentJobs.slice(0, 10)}
          loading={loading}
          emptyMessage={t('projectServices.dashboard.noRecentCompletions')}
          configMap={configMap}
          subtitle={t('projectServices.dashboard.recentCompletionsSubtitle')}
          columns={jobColumns}
          isSmallScreen={isSmallScreen}
          t={t}
        />
      </Stack>
    </>
  );
}

function ServicesDashboardView(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    isSuperUser,
    permissions: { isCrewManager, isAccounting, isTechSupport, isTechnician },
  } = useUser();

  const loading = useSelector(selectDashboardLoading);
  const pendingJobs = useSelector(selectDashboardPendingJobs);
  const recentJobs = useSelector(selectDashboardRecentJobs);
  const stats = useSelector(selectDashboardStats);

  // Tenant-level terms: the dashboard lists jobs across customers, so no
  // single CustomerType override applies — the tenant vocabulary does.
  const { customer_term: customerTerm } = useSelector(selectTenantInfo);
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();

  const isStaffView = isCrewManager || isAccounting || isTechSupport;

  // Map customerId → CustomerJobConfig.id for building URLs
  const [configMap, setConfigMap] = useState({});
  const [jobConfigurations, setJobConfigurations] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchDashboardData());
    dispatch(fetchJobConfigurationsWithFilters({ limit: 100, offset: 0 }))
      .then(data => {
        if (data && !data.error) {
          const configs = Array.isArray(data) ? data : [];
          setJobConfigurations(configs);
          const map = {};
          configs.forEach(config => {
            const custId = _.get(config, ['customer', 'id']);
            if (custId) {
              map[custId] = config.id;
            }
          });
          setConfigMap(map);
        }
      })
      .finally(() => {
        setProjectsLoading(false);
      });
  }, []);

  const jobColumns = useMemo(
    () =>
      [
        {
          field: 'site_group',
          headerName: siteGroupTerm.singular,
          flex: 1,
          minWidth: 120,
          disableColumnMenu: true,
          valueGetter: (value, row) => _.get(row, ['site', 'site_groups', 0, 'name'], ''),
        },
        {
          field: 'site',
          headerName: siteTerm.singular,
          flex: 1,
          minWidth: 120,
          disableColumnMenu: true,
          valueGetter: value => _.get(value, 'name', t('projectServices.dashboard.unknownSite')),
        },
        !isSmallScreen && {
          field: 'crew',
          headerName: t('projectServices.dashboard.columns.crew'),
          width: 100,
          disableColumnMenu: true,
          valueGetter: value => _.get(value, 'crew_number', t('common.notAvailable')),
        },
        {
          field: 'pending',
          headerName: t('projectServices.dashboard.columns.status'),
          width: 100,
          disableColumnMenu: true,
          renderCell: params => {
            const isPending = params.value === true;
            return isPending ? (
              <GridStatusCell label={t('projectServices.dashboard.status.pending')} color="warning" />
            ) : (
              <GridStatusCell label={t('projectServices.dashboard.status.completed')} color="ok" />
            );
          },
        },
        !isSmallScreen && {
          field: 'submitted_by',
          headerName: t('projectServices.dashboard.columns.submittedBy'),
          flex: 1,
          minWidth: 150,
          disableColumnMenu: true,
          valueGetter: value =>
            value
              ? formatUserString(fromJS(value))
              : t('projectServices.dashboard.unknownCustomer', { term: customerTerm }),
        },
        !isSmallScreen && {
          field: 'installation_date',
          headerName: t('projectServices.dashboard.columns.date'),
          width: 130,
          disableColumnMenu: true,
          valueFormatter: value => value || t('common.notAvailable'),
        },
      ].filter(Boolean),
    [t, isSmallScreen, siteTerm, siteGroupTerm, customerTerm],
  );

  const sharedProps = {
    jobConfigurations,
    projectsLoading,
    showTooltipInfo: isSuperUser,
    t,
    jobColumns,
    isSmallScreen,
  };

  return (
    <AppNav notFullHeight page="services_dashboard" {...props}>
      <Box
        sx={{
          top: '64px',
          mx: 2,
          pb: '24px',
        }}
      >
        <Box>
          <TitleBar title={t('projectServices.dashboard.pageTitle')} />
        </Box>
        <Box sx={{ mt: 0.5 }}>
          {isStaffView ? (
            <StaffDashboard
              loading={loading}
              pendingJobs={pendingJobs}
              recentJobs={recentJobs}
              stats={stats}
              isCrewManager={isCrewManager}
              isAccounting={isAccounting}
              configMap={configMap}
              {...sharedProps}
            />
          ) : (
            <TechnicianDashboard
              loading={loading}
              pendingJobs={pendingJobs}
              recentJobs={recentJobs}
              configMap={configMap}
              {...sharedProps}
            />
          )}
        </Box>
      </Box>
    </AppNav>
  );
}

export default ServicesDashboardView;
